import type { SupabaseClient } from '@supabase/supabase-js';
import type { RankingSource } from '../core/types.ts';
import { cacheAccountSnapshot, loadCachedAccountSnapshot } from './localCache.ts';
import { SyncConflictError, validateProfile, validateSnapshot, validateSources } from './validation.ts';
import { ACCOUNT_SYNC_SCHEMA_VERSION, type AccountSnapshot, type SleeperProfile, type SyncRepository } from './types.ts';

interface ProfileRow {
  sleeper_user_id: string;
  sleeper_username: string;
  setup: SleeperProfile['setup'];
  setup_version: number;
  updated_at: string;
  last_used_at: string | null;
}

interface SourceRow {
  source_id: string;
  payload: RankingSource;
  payload_version: number;
  updated_at: string;
}

interface ProfileSaveResult {
  status: 'saved' | 'conflict';
  profile?: ProfileRow;
}

interface SourceSaveResult {
  status: 'saved' | 'conflict';
  source_versions?: Record<string, string>;
}

export class SupabaseSyncRepository implements SyncRepository {
  private readonly client: SupabaseClient;
  private readonly userId: string;
  private sourceUpdatedAt = new Map<string, string | null>();

  constructor(client: SupabaseClient, userId: string) {
    this.client = client;
    this.userId = userId;
  }

  async loadSnapshot(options: { cache?: boolean } = {}): Promise<AccountSnapshot> {
    const [profiles, sources] = await Promise.all([
      this.client
        .from('sleeper_profiles')
        .select('sleeper_user_id,sleeper_username,setup,setup_version,updated_at,last_used_at')
        .eq('user_id', this.userId),
      this.client
        .from('ranking_sources')
        .select('source_id,payload,payload_version,updated_at')
        .eq('user_id', this.userId),
    ]);
    if (profiles.error) throw profiles.error;
    if (sources.error) throw sources.error;

    const profileRows = profiles.data as ProfileRow[];
    const sourceRows = sources.data as SourceRow[];
    if (profileRows.some((row) => row.setup_version !== ACCOUNT_SYNC_SCHEMA_VERSION) || sourceRows.some((row) => row.payload_version !== ACCOUNT_SYNC_SCHEMA_VERSION)) {
      throw new Error('Saved account data uses an unsupported version.');
    }
    const userSourceRows = sourceRows.filter((row) => row.payload.kind === 'user');
    const snapshot: AccountSnapshot = {
      profiles: profileRows.map((row) => ({
        sleeper_user_id: row.sleeper_user_id,
        sleeper_username: row.sleeper_username,
        setup: row.setup,
        updated_at: row.updated_at,
        last_used_at: row.last_used_at,
      })),
      sources: userSourceRows.map((row) => row.payload),
      source_versions: Object.fromEntries(userSourceRows.map((row) => [row.source_id, row.updated_at])),
    };
    validateSnapshot(snapshot);
    this.sourceUpdatedAt = new Map(userSourceRows.map((row) => [row.source_id, row.updated_at]));
    if (options.cache !== false) await cacheAccountSnapshot(this.userId, snapshot);
    return snapshot;
  }

  async replaceSnapshot(snapshot: AccountSnapshot): Promise<AccountSnapshot> {
    validateSnapshot(snapshot);
    const userSources = snapshot.sources.filter((source) => source.kind === 'user');
    validateSources(userSources);
    const result = await this.client.rpc('replace_account_snapshot', {
      p_profiles: snapshot.profiles,
      p_sources: userSources.map((source) => ({ source_id: source.source_id, payload: source })),
    });
    if (result.error) throw result.error;
    // The RPC is transactional; reload also gives the client fresh updated_at
    // values for subsequent optimistic-concurrency checks.
    return this.loadSnapshot();
  }

  async saveProfile(profile: SleeperProfile, expectedUpdatedAt: string | null = null): Promise<SleeperProfile> {
    validateProfile(profile);
    const result = await this.client.rpc('save_sleeper_profile_if_current', {
      p_sleeper_user_id: profile.sleeper_user_id,
      p_sleeper_username: profile.sleeper_username,
      p_setup: profile.setup,
      p_last_used_at: profile.last_used_at,
      p_expected_updated_at: expectedUpdatedAt,
    });
    if (result.error) throw result.error;
    const saved = result.data as ProfileSaveResult;
    if (saved.status === 'conflict' || !saved.profile) throw new SyncConflictError();
    const row = saved.profile;
    if (row.setup_version !== ACCOUNT_SYNC_SCHEMA_VERSION) throw new Error('Saved account data uses an unsupported version.');
    return row as SleeperProfile;
  }

  async saveSources(sources: RankingSource[]): Promise<Record<string, string>> {
    const userSources = sources.filter((source) => source.kind === 'user');
    validateSources(userSources);
    const result = await this.client.rpc('replace_ranking_sources_if_current', {
      p_sources: userSources.map((source) => ({ source_id: source.source_id, payload: source })),
      p_expected_versions: Object.fromEntries(
        Array.from(this.sourceUpdatedAt.entries()).filter((entry): entry is [string, string] => entry[1] !== null),
      ),
    });
    if (result.error) throw result.error;
    const saved = result.data as SourceSaveResult;
    if (saved.status === 'conflict' || !saved.source_versions) {
      throw new SyncConflictError('A ranking source changed on another device.');
    }
    this.sourceUpdatedAt = new Map(Object.entries(saved.source_versions));
    return saved.source_versions;
  }

  setCachedSourceVersions(versions: Record<string, string | null>): void {
    this.sourceUpdatedAt = new Map(Object.entries(versions));
  }

}

/**
 * Read-through cache used when Supabase is temporarily unavailable. This is
 * intentionally a separate helper so callers can keep the draft functional
 * without pretending that an offline write was synced.
 */
export async function loadAccountSnapshotWithCache(
  userId: string,
  repository: SyncRepository,
): Promise<{ snapshot: AccountSnapshot; fromCache: boolean }> {
  try {
    return { snapshot: await repository.loadSnapshot(), fromCache: false };
  } catch {
    return { snapshot: await loadCachedAccountSnapshot(userId), fromCache: true };
  }
}
